import { useEffect, useRef, useState } from 'react';
import {
  NOTE_FORMATS,
  NOTE_FORMAT_LABELS,
  type GroupAttendeeView,
  type GroupCosignResult,
  type GroupSessionView,
  type GroupSubmitResult,
  type SupervisionView,
} from '@ehr/shared';
import { api, ApiError } from '../lib/api.js';
import { useSession, canManage } from '../auth/session.js';
import { Dd } from './Dropdown.js';
import { ProgressNoteEditor, quickList, type ProgressNote } from './ClinicalNotesPanel.js';
import { ConfirmDialog } from './ConfirmDialog.js';
import {
  AttendanceWindow,
  RosterGroups,
  SignReadyWindow,
  type AttendanceRow,
} from './SessionTools.js';
import type { NoteUser } from './noteSharing.js';
import { fetchNoteUsers } from '../lib/noteUsers.js';
import {
  draftAttendee,
  generateEveryoneBlock,
  memberNoteOpen,
  type Body,
} from '../lib/groupNotes.js';
import { readSuggestions } from '../lib/trackChanges.js';
import { useNoteTabLock } from '../lib/useNoteTabLock.js';
import { groupTextFor, otherMembers } from '../lib/groupNoteV26.js';
import {
  attendanceLabel,
  attendeeLeft,
  bodyText,
  lateHoursOf,
  rosterLine,
  type RosterLine,
} from '../lib/sessionRoster.js';
import { noteBlocks } from '../lib/noteLayouts.js';
import { timeOpts } from '../lib/timePrefs.js';

const errText = (e: unknown, fallback: string) => (e instanceof ApiError ? e.message : fallback);
const first = (name: string) => name.split(' ')[0] ?? name;
const plain = (html: unknown) =>
  String(html ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
const attendanceOfBody = (b: Record<string, unknown> | undefined) =>
  (b?.attendance as { status?: string } | undefined)?.status;
const OPEN = new Set(['draft', 'changes']);

/**
 * ehr#437 (GROUP_NOTE_10-14-V26): a group session as the V81 note page, like the Family & Couples
 * note. The group bar holds the roster (Needs you / Ready to sign / Signed, with walk-ins), the
 * group's template, the Group summary (the same on every member's note, its member names flagged)
 * and the session tools (Generate everyone from the group's notes, Sign ready notes). Below it, one
 * member's note in the progress note page; switching member saves first. Opened from the Calendar
 * group appointment (/?group=<eventId>@<YYYY-MM-DD>), where the older workspace used to open.
 *
 * ehr#571 round 2 (GROUP_NOTE_10-20-V22): the session line names the code and the facilitator; the
 * roster counts each group and says what each member's note still needs; Attendance marks everyone
 * at once; Sign ready notes asks first (late-entry reason, attest); after a member's note is signed
 * the next one that needs something opens.
 *
 * ehr#688 (GROUP_NOTE_10-29-V25): the tools say Generate, not Draft; Apply to <name>'s note
 * generates the open member's note only; Generate everyone is for the group's lead clinician and is
 * off when no member's note is open; the roster says when a note was signed, which round a
 * resubmitted note is on and how many comments a returned note still has open.
 *
 * ehr#688 (V25 "V20 20a", "V22 read-only"): the reviewer's Co-sign all ready notes co-signs each
 * note pending review that they can (POST /group-notes/:id/cosign) and lists what was skipped and
 * why. One editable copy of a group session per browser (lib/noteTabLock, key group:<sessionId>,
 * as #686 does for a progress note): a second tab opens the whole session read-only, with Open
 * here instead.
 */
export function GroupNotePage({
  appointmentKey,
  focusContactId,
  onClose,
}: {
  appointmentKey: string;
  focusContactId?: string | null;
  onClose: () => void;
}) {
  const { user } = useSession();
  const [view, setView] = useState<GroupSessionView | null>(null);
  const [loadErr, setLoadErr] = useState('');
  const [users, setUsers] = useState<NoteUser[]>([]);
  const [current, setCurrent] = useState<string | null>(focusContactId ?? null);
  const [history, setHistory] = useState<ProgressNote[]>([]);
  const [summary, setSummary] = useState('');
  const [rough, setRough] = useState('');
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [rev, setRev] = useState(0);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [q, setQ] = useState('');
  const [found, setFound] = useState<{ id: string; firstName: string; lastName: string }[]>([]);
  const [attOpen, setAttOpen] = useState(false);
  const [signAll, setSignAll] = useState(false);
  const [removing, setRemoving] = useState<GroupAttendeeView | null>(null);
  const [lateLimit, setLateLimit] = useState(72);
  const [supervised, setSupervised] = useState(false);
  const [cosignAsk, setCosignAsk] = useState(false);
  const [cosignOut, setCosignOut] = useState<string[] | null>(null);
  const saveRef = useRef<() => Promise<void>>(async () => undefined);
  const me = user?.id ?? null;
  const isManager = Boolean(user && canManage(user.role));

  const adopt = (v: GroupSessionView) => {
    setView(v);
    setSummary((s) => (s === '' ? v.groupSummary : s));
    // the group's own notes for NotePilot (kept on the session, as the old workspace did)
    setRough((r) => (r === '' ? plain(v.sessionSummary) : r));
    setCurrent((c) => c ?? v.attendees[0]?.contactId ?? null);
    return v;
  };
  const reload = () =>
    view ? api.get<GroupSessionView>(`/group-notes/${view.id}`).then(adopt) : Promise.resolve(null);
  // ehr#688: the session as saved (by the other copy): its shared text too, and the open note again
  const refresh = () =>
    view
      ? api.get<GroupSessionView>(`/group-notes/${view.id}`).then((v) => {
          setView(v);
          setSummary(v.groupSummary);
          setRough(plain(v.sessionSummary));
          setRev((x) => x + 1);
        })
      : Promise.resolve();
  // ehr#688 (V25 tab lock, as #686 for a progress note): one editable copy of this group session in
  // this browser. A second tab or window opens it read-only until it is free or takes it over.
  const tabLock = useNoteTabLock(view ? `group:${view.id}` : null, {
    // another copy took the session: save what this one has unsaved first
    onLost: async () => {
      if (!view) return false;
      try {
        await saveRef.current();
        const lead0 = view.leadId === me || isManager;
        const text: { groupSummary?: string; sessionSummary?: string } = {};
        if (lead0 && summary !== view.groupSummary) text.groupSummary = summary;
        if (lead0 && rough !== plain(view.sessionSummary)) text.sessionSummary = rough;
        if (Object.keys(text).length) await api.patch(`/group-notes/${view.id}`, text);
        return true;
      } catch {
        return false;
      }
    },
    onRegained: (reason) => {
      void refresh();
      setMsg(
        reason === 'other-closed'
          ? 'The other copy closed. This copy can be edited now.'
          : 'This copy can be edited now.',
      );
    },
    onRemoteSaved: () => void refresh(),
  });
  const ro = tabLock.readOnly;
  /** ehr#688: something was saved here, so a read-only copy in another tab reloads. */
  const notify = () => tabLock.notifySaved();

  useEffect(() => {
    let live = true;
    api
      .post<GroupSessionView>('/group-notes/open', { appointmentKey })
      .then((v) => {
        if (!live) return;
        adopt(v);
        // ehr#688: a reviewer with notes to co-sign finds Co-sign all ready notes open
        if (v.attendees.some((a) => a.note?.canCosign)) setToolsOpen(true);
      })
      .catch((e: unknown) => live && setLoadErr(errText(e, 'Could not open this group session.')));
    // ehr#571: names for clinicians too (they can't read /settings/users).
    void fetchNoteUsers().then((u) => live && setUsers(u));
    // ehr#571 (V141): the practice's late-entry hours, for Sign ready notes
    api
      .get<{ lateEntryHours?: number } | null>('/practice/clinical')
      .then((c) => live && typeof c?.lateEntryHours === 'number' && setLateLimit(c.lateEntryHours))
      .catch(() => undefined);
    api
      .get<SupervisionView>('/progress-notes/supervision')
      .then((s) => live && setSupervised(Boolean(s?.required)))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [appointmentKey]);
  // the open member's own notes (Last session, From your notes)
  useEffect(() => {
    if (!current) return;
    let live = true;
    api
      .get<ProgressNote[]>(`/contacts/${current}/progress-notes`)
      .then((h) => live && setHistory(h))
      .catch(() => live && setHistory([]));
    return () => {
      live = false;
    };
  }, [current]);
  useEffect(() => {
    if (!adding || q.trim().length < 2) {
      setFound([]);
      return;
    }
    let live = true;
    const t = window.setTimeout(() => {
      api
        .get<{ items: { id: string; firstName: string; lastName: string }[] }>(
          `/contacts?search=${encodeURIComponent(q.trim())}`,
        )
        .then((r) => live && setFound((r.items ?? []).slice(0, 8)))
        .catch(() => live && setFound([]));
    }, 250);
    return () => {
      live = false;
      window.clearTimeout(t);
    };
  }, [q, adding]);

  if (!view || !current)
    return (
      <div className="pn-page" role="dialog" aria-modal="true" aria-label="Group note">
        <div className="pn-page-in pn-page-grid">
          <p className="muted">{loadErr || 'Opening the group session…'}</p>
          {loadErr && (
            <button type="button" className="btn" onClick={onClose}>
              Back
            </button>
          )}
        </div>
      </div>
    );

  const meA = view.attendees.find((a) => a.contactId === current) ?? view.attendees[0]!;
  const lead = view.leadId === me || isManager;
  const sessionDate = view.startsAt.toString().slice(0, 10);
  const when = new Date(view.startsAt);
  const ends = new Date(when.getTime() + view.durationMin * 60_000);
  const textKeys = noteBlocks(view.format, 'Group')
    .filter((b) => b.kind === 'text')
    .map((b) => b.id);
  const boxesOf = (a: GroupAttendeeView) =>
    Object.values(bodyText((a.note?.body ?? {}) as Body, textKeys));
  const summaryWritten = summary.trim() !== '';
  // V22 groupOwnItems: the Group summary and everyone else's notes
  const sharedFor = (cid: string) => [
    summary,
    ...view.attendees.filter((x) => x.contactId !== cid).flatMap(boxesOf),
  ];
  const absentOf = (a: GroupAttendeeView) =>
    attendanceOfBody(a.note?.body as Record<string, unknown> | undefined) === 'Not present';
  const lineOf = (a: GroupAttendeeView): RosterLine => {
    const b = (a.note?.body ?? {}) as Body;
    const absent = absentOf(a);
    const left = a.note
      ? attendeeLeft(b, {
          format: view.format,
          kind: 'Group',
          quick: quickList(b.quick),
          absent,
          group: {
            members: otherMembers(view.attendees, a.contactId),
            summaryWritten,
            shared: sharedFor(a.contactId),
            firstName: first(a.name),
          },
        })
      : [];
    // ehr#688 (V25): the sign time, the review round and the reviewer's open comments
    const at =
      a.note?.status === 'cosigned'
        ? a.note.cosignedAt
        : a.note?.status === 'signed'
          ? a.note.submittedAt
          : null;
    return rosterLine({
      status: a.note?.status ?? null,
      left,
      absent,
      absentLabel: 'Did not attend',
      time: at ? new Date(at).toLocaleTimeString('en-US', timeOpts()) : undefined,
      round: a.note?.reviewRound,
      openComments: readSuggestions(b.suggestions)?.comments.length ?? 0,
    });
  };
  const lines = new Map(view.attendees.map((a) => [a.contactId, lineOf(a)]));
  const attended = view.attendees.filter(
    (a) => !absentOf(a) && a.rosterStatus !== 'removed',
  ).length;
  const ready = view.attendees.filter(
    (a) => a.note && OPEN.has(a.note.status) && lines.get(a.contactId)!.group === 'ready',
  );
  const lateOf = (a: GroupAttendeeView) =>
    lateHoursOf((a.note?.body ?? {}) as Body, sessionDate, lateLimit, new Date());
  const lateReady = ready.map(lateOf).filter((h): h is number => h !== null);
  // ehr#688 (V25 groupAiBody): why Generate everyone is off, if it is
  const genBlock = generateEveryoneBlock({
    lead,
    readOnly: ro,
    attendees: view.attendees,
    me,
    isManager,
  });
  const run = (p: Promise<unknown>, fallback: string) => {
    setErr('');
    return p.catch((e: unknown) => setErr(errText(e, fallback)));
  };
  const switchTo = (cid: string) => {
    if (cid === current) return;
    setMsg('');
    void run(
      saveRef
        .current()
        .then(reload)
        .then(() => setCurrent(cid)),
      'Could not save this note first.',
    );
  };
  const patch = (p: { format?: string; groupSummary?: string; sessionSummary?: string }) =>
    run(
      api.patch<GroupSessionView>(`/group-notes/${view.id}`, p).then((v) => {
        notify();
        return adopt(v);
      }),
      'Could not save.',
    );
  const walkIns = (p: { add?: string[]; remove?: string[] }) =>
    run(
      api.put<GroupSessionView>(`/group-notes/${view.id}/walk-ins`, p).then((v) => {
        notify();
        adopt(v);
        setAdding(false);
        setQ('');
        if (p.remove?.includes(current)) setCurrent(v.attendees[0]?.contactId ?? null);
      }),
      'Could not change the walk-ins.',
    );
  // ehr#688 (V25): may this person change a member's note from the bar (Generate everyone, Apply)
  const openToMe = (a: GroupAttendeeView) => !ro && memberNoteOpen(a, me, isManager);
  // NotePilot generates the empty boxes of each listed member's note (all of them when none are
  // listed) from the group's own notes; returns how many notes it wrote.
  const generateFor = async (only: string | null, text: string) => {
    await saveRef.current();
    // the notes as saved just now (the open one included), never the ones on screen before
    const fresh = await api.get<GroupSessionView>(`/group-notes/${view.id}`);
    let n = 0;
    for (const a of fresh.attendees) {
      if (only ? a.contactId !== only : a.rosterStatus === 'removed') continue;
      if (!openToMe(a)) continue;
      const body = (a.note?.body ?? {}) as Body;
      // V22 groupRoughFor: without sentences naming another member, plus their own notes
      const own = groupTextFor(
        text,
        otherMembers(fresh.attendees, a.contactId),
        plain(body.sessionSummary),
      );
      if (!own) continue;
      const r = draftAttendee(body, view.format, own, {
        durationMin: view.durationMin,
        approaches: view.approaches,
        clientName: a.name,
      });
      if (!r.drafted) continue;
      await api.put(`/group-notes/${view.id}/attendees/${a.contactId}/note`, {
        body: r.body,
      });
      n++;
    }
    if (n) notify();
    await reload();
    setRev((x) => x + 1);
    return n;
  };
  // Generate everyone (V26, renamed in ehr#688 for V25): every open member's note.
  const generateEveryone = () => {
    if (genBlock) return setErr(genBlock);
    const text = rough.trim();
    if (!text) return setErr('Write what happened in the group first, then Generate everyone.');
    setBusy(true);
    void run(
      generateFor(null, text)
        .then((n) =>
          setMsg(
            n
              ? `Generated ${n} member note${n === 1 ? '' : 's'}. Read each one and make it theirs.`
              : 'Nothing to generate: every open member’s boxes have writing.',
          ),
        )
        .finally(() => setBusy(false)),
      'Could not generate everyone.',
    );
  };
  // ehr#688 (V25 Apply to <name>'s note): the open member's note only, from the group's notes
  // without the sentences naming someone else, plus the member's own.
  const applyToMember = () => {
    if (!openToMe(meA)) return;
    const name = first(meA.name);
    setBusy(true);
    void run(
      generateFor(meA.contactId, rough.trim())
        .then((n) =>
          setMsg(
            n
              ? `Generated ${name}’s note. Read it and make it theirs.`
              : `Nothing to generate for ${name}: write what happened in the group first, or ${name}’s boxes already have writing.`,
          ),
        )
        .finally(() => setBusy(false)),
      `Could not generate ${name}’s note.`,
    );
  };
  // V22 openSignAll: the ready notes only, each signed as usual; a late one keeps the reason.
  const signReady = (reason: string) => {
    setBusy(true);
    void run(
      saveRef
        .current()
        .then(async () => {
          const fresh = await api.get<GroupSessionView>(`/group-notes/${view.id}`);
          const ids = new Set(ready.map((a) => a.contactId));
          for (const a of fresh.attendees) {
            if (!ids.has(a.contactId) || !a.note) continue;
            const hours = lateOf(a);
            if (hours === null) continue;
            await api.put(`/group-notes/${view.id}/attendees/${a.contactId}/note`, {
              body: { ...a.note.body, lateEntry: { hours, reason } },
            });
          }
          const r = await api.post<GroupSubmitResult>(`/group-notes/${view.id}/submit`, {
            contactIds: [...ids],
          });
          setSignAll(false);
          notify();
          await reload();
          setRev((x) => x + 1);
          const nameOf = (cid: string) =>
            first(view.attendees.find((a) => a.contactId === cid)?.name ?? cid);
          const verb = supervised ? 'Submitted' : 'Signed';
          setMsg(
            [
              r.submitted.length ? `${verb} ${r.submitted.length}.` : 'None were ready.',
              ...r.held.map((h) => `${nameOf(h.contactId)}: ${h.reason}`),
            ].join(' '),
          );
        })
        .finally(() => setBusy(false)),
      'Could not sign the notes.',
    );
  };
  // ehr#688 (V25 openCosignAll): the reviewer co-signs every note pending review they can, at once;
  // each is co-signed on its own, and one that can't be is skipped with the reason.
  const cosignable = view.attendees.filter((a) => a.note?.canCosign);
  const cosignAll = () => {
    setCosignAsk(false);
    setCosignOut(null);
    setMsg('');
    setBusy(true);
    void run(
      saveRef
        .current()
        .then(async () => {
          const r = await api.post<GroupCosignResult>(`/group-notes/${view.id}/cosign`, {
            contactIds: cosignable.map((a) => a.contactId),
          });
          if (r.cosigned.length) notify();
          await reload();
          setRev((x) => x + 1);
          const nameOf = (cid: string) =>
            view.attendees.find((a) => a.contactId === cid)?.name ?? cid;
          const n = r.cosigned.length;
          setMsg(
            n
              ? `Co-signed ${n} note${n === 1 ? '' : 's'}. ${n === 1 ? 'It is' : 'They are'} locked.`
              : 'No note was co-signed.',
          );
          setCosignOut([
            ...r.cosigned.map((c) => `${nameOf(c.contactId)}: Co-signed`),
            ...r.skipped.map((x) => `${nameOf(x.contactId)}: Skipped. ${x.reason}`),
          ]);
        })
        .finally(() => setBusy(false)),
      'Could not co-sign the notes.',
    );
  };
  // V22 46 A: everyone's attendance at once (each changed note saves with its new attendance)
  const saveAttendance = (changed: Record<string, string>) => {
    const ids = Object.keys(changed);
    if (!ids.length) return setAttOpen(false);
    setBusy(true);
    void run(
      saveRef
        .current()
        .then(async () => {
          const fresh = await api.get<GroupSessionView>(`/group-notes/${view.id}`);
          for (const cid of ids) {
            const a = fresh.attendees.find((x) => x.contactId === cid);
            if (!a || (a.note && !OPEN.has(a.note.status))) continue;
            const body = a.note
              ? { ...a.note.body, attendance: { status: changed[cid] } }
              : {
                  noteType: 'Group',
                  attendance: { status: changed[cid] },
                  session: {
                    time: when.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
                    type: 'Group Therapy',
                    durationMin: view.durationMin,
                    place: 'In person',
                  },
                };
            await api.put(`/group-notes/${view.id}/attendees/${cid}/note`, { body });
          }
          setAttOpen(false);
          notify();
          await reload();
          setRev((x) => x + 1);
          setMsg(`Attendance saved for ${ids.length} ${ids.length === 1 ? 'person' : 'people'}.`);
        })
        .finally(() => setBusy(false)),
      'Could not save the attendance.',
    );
  };
  // V22 30 A: after one note is signed, the next member who still needs something opens.
  const nextAfterSign = () =>
    void reload().then((v) => {
      if (!v) return;
      const i = v.attendees.findIndex((a) => a.contactId === current);
      const order = [...v.attendees.slice(i + 1), ...v.attendees.slice(0, Math.max(0, i))];
      const next = order.find((a) => !a.note || OPEN.has(a.note.status));
      const done = supervised ? 'Submitted.' : 'Signed.';
      if (!next) return setMsg(done);
      setCurrent(next.contactId);
      setMsg(`${done} Next: ${next.name}.`);
    });
  const lead1 = users.find((u) => u.id === view.leadId);
  const facilitator = lead1 ? [lead1.firstName, lead1.lastName].filter(Boolean).join(' ') : '';
  const attRows: AttendanceRow[] = view.attendees
    .filter((a) => a.rosterStatus !== 'removed')
    .map((a) => ({
      contactId: a.contactId,
      name: a.name,
      value: absentOf(a)
        ? 'Not present'
        : ((attendanceOfBody(a.note?.body as Record<string, unknown> | undefined) ??
            'Attended') as AttendanceRow['value']),
      locked: !!a.note && !OPEN.has(a.note.status),
    }));
  const allLocked = attRows.every((r) => r.locked);

  const bar = (
    <section className="pn-sec pn-fam pn-grp" aria-label="Group session">
      {/* ehr#688 (V25, as #686): a second copy of the group session is read-only until it is free */}
      {ro && (
        <div className="pn-banner lock pn-tablock" role="status">
          <span>
            This group note is open in another tab. This copy is read-only, so the two can&rsquo;t
            save over each other.
          </span>
          <button
            type="button"
            className="btn small primary"
            onClick={tabLock.takeOver}
            disabled={tabLock.state === 'opening'}
          >
            {tabLock.state === 'opening' ? 'Opening here...' : 'Open here instead'}
          </button>
        </div>
      )}
      <div className="pn-sec-h">
        <h3>{view.title}</h3>
        <span className="pn-sec-sum pn-sess-line">
          <span>{when.toLocaleDateString('en-US')}</span>
          <span>
            {when.toLocaleTimeString('en-US', timeOpts())} to{' '}
            {ends.toLocaleTimeString('en-US', timeOpts())} ({view.durationMin} min)
          </span>
          {view.location && <span>{view.location}</span>}
          <span>90853 · Group psychotherapy</span>
          {facilitator && <span>Facilitator: {facilitator}</span>}
        </span>
        <span className="pn-sp" />
        <div className="pn-grp-tpl">
          <Dd
            value={view.format}
            disabled={!lead || ro}
            searchable={false}
            ariaLabel="The group's template"
            onChange={(v) => v && v !== view.format && void patch({ format: v })}
            options={NOTE_FORMATS.map((f) => ({ value: f, label: NOTE_FORMAT_LABELS[f] }))}
          />
        </div>
      </div>
      <div className="pn-grp-roster" aria-label="Roster">
        <div className="pn-grp-roster-h">
          <b>Roster</b>
          <span className="muted">
            {attended} of {view.attendees.length} attended
          </span>
          {!allLocked && !ro && (
            <button type="button" className="pn-link" onClick={() => setAttOpen(true)}>
              Attendance
            </button>
          )}
        </div>
        <RosterGroups
          entries={view.attendees.map((a) => ({
            contactId: a.contactId,
            name: a.name,
            line: lines.get(a.contactId)!,
            tags: a.walkIn ? <span className="pn-tag">Walk-in</span> : null,
          }))}
          current={meA.contactId}
          onPick={switchTo}
        />
        <div className="pn-grp-roster-f">
          {ro ? null : !adding ? (
            <button type="button" className="pn-link" onClick={() => setAdding(true)}>
              + Add walk-in
            </button>
          ) : (
            <div className="pn-fam-search">
              <input
                autoFocus
                value={q}
                placeholder="Search the practice's clients"
                aria-label="Search clients"
                onChange={(e) => setQ(e.target.value)}
              />
              {found
                .filter((c) => !view.attendees.some((a) => a.contactId === c.id))
                .map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className="pn-fam-pick"
                    onClick={() => void walkIns({ add: [c.id] })}
                  >
                    {[c.firstName, c.lastName].filter(Boolean).join(' ')}
                  </button>
                ))}
              <button type="button" className="pn-link" onClick={() => setAdding(false)}>
                Cancel
              </button>
            </div>
          )}
          {!ro && meA.walkIn && (!meA.note || meA.note.status === 'draft') && (
            <button type="button" className="pn-link danger" onClick={() => setRemoving(meA)}>
              Remove {first(meA.name)} (walk-in)
            </button>
          )}
        </div>
      </div>
      <div className="pn-fam-sum" data-pn-target="gsum">
        <div className="pn-nec-h">
          <span>Group summary</span>
          <span className="pn-tag">Same on every member&rsquo;s note</span>
        </div>
        <textarea
          value={summary}
          readOnly={!lead || ro}
          placeholder="What the group did: topic, skills, interventions. No member's name."
          aria-label="Group summary"
          onChange={(e) => setSummary(e.target.value)}
          onBlur={() => summary !== view.groupSummary && void patch({ groupSummary: summary })}
        />
        {view.nameFlags.length > 0 && (
          <p className="pn-inline-err" role="alert">
            The Group summary names {view.nameFlags.join(' and ')}. It goes on every member&rsquo;s
            note: take the name out.
          </p>
        )}
      </div>
      <details
        className="pn-grp-tools"
        open={toolsOpen}
        onToggle={(e) => setToolsOpen((e.currentTarget as HTMLDetailsElement).open)}
      >
        <summary>Generate everyone, sign ready notes</summary>
        <textarea
          value={rough}
          placeholder="The group's own notes: what happened, for NotePilot to generate each member's empty boxes."
          aria-label="Group notes for NotePilot"
          readOnly={!lead || ro}
          onChange={(e) => setRough(e.target.value)}
          onBlur={() =>
            lead && rough !== view.sessionSummary && void patch({ sessionSummary: rough })
          }
        />
        <div className="pn-grp-tools-act">
          <button
            type="button"
            className="btn"
            disabled={busy || !!genBlock}
            title={genBlock ?? undefined}
            onClick={generateEveryone}
          >
            Generate everyone
          </button>
          <button
            type="button"
            className="btn"
            disabled={busy || !openToMe(meA)}
            title={
              openToMe(meA)
                ? undefined
                : `${first(meA.name)}’s note is locked or not yours to write.`
            }
            onClick={applyToMember}
          >
            Apply to {first(meA.name)}&rsquo;s note
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={busy || ro || !ready.length}
            onClick={() => setSignAll(true)}
          >
            {supervised ? 'Submit' : 'Sign'} {ready.length ? `${ready.length} ` : ''}ready note
            {ready.length === 1 ? '' : 's'}
          </button>
          {cosignable.length > 0 && (
            <button
              type="button"
              className="btn primary"
              data-test="cosign-all"
              disabled={busy || ro}
              onClick={() => setCosignAsk(true)}
            >
              Co-sign all ready notes ({cosignable.length})
            </button>
          )}
        </div>
        {genBlock && <p className="muted pn-grp-why">{genBlock}</p>}
      </details>
      {err && (
        <p className="pn-inline-err" role="alert">
          {err}
        </p>
      )}
      {msg && !err && (
        <p className="pn-fam-msg" role="status">
          {msg}
        </p>
      )}
      {cosignOut && cosignOut.length > 0 && (
        <ul className="pn-grp-cosign-out" aria-label="Co-sign results">
          {cosignOut.map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ul>
      )}
      {cosignAsk && (
        <ConfirmDialog
          message={
            <>
              <b>
                Co-sign {cosignable.length} ready note{cosignable.length === 1 ? '' : 's'}?
              </b>
              <br />
              {cosignable.map((a) => a.name).join(', ')}. Each note is co-signed on its own and
              locked. A note that can&rsquo;t be co-signed yet (a required part still blank) is
              skipped, with the reason.
            </>
          }
          confirmLabel="Co-sign all"
          danger={false}
          onConfirm={cosignAll}
          onClose={() => setCosignAsk(false)}
        />
      )}
      {attOpen && (
        <AttendanceWindow
          rows={attRows}
          label={attendanceLabel}
          busy={busy}
          onSave={saveAttendance}
          onClose={() => setAttOpen(false)}
        />
      )}
      {signAll && (
        <SignReadyWindow
          names={ready.map((a) => a.name)}
          lateHours={lateReady.length ? Math.max(...lateReady) : null}
          supervised={supervised}
          busy={busy}
          onConfirm={signReady}
          onClose={() => setSignAll(false)}
        />
      )}
      {removing && (
        <ConfirmDialog
          message={
            <>
              <b>Remove &ldquo;{removing.name}&rdquo; from this group?</b>
              <br />
              Their note for this group session is deleted. This can&rsquo;t be undone.
            </>
          }
          confirmLabel="Remove walk-in"
          onConfirm={() => {
            const cid = removing.contactId;
            setRemoving(null);
            void walkIns({ remove: [cid] });
          }}
          onClose={() => setRemoving(null)}
        />
      )}
    </section>
  );

  return (
    <ProgressNoteEditor
      key={`${meA.contactId}:${meA.note?.id ?? 'new'}:${rev}`}
      editId={meA.note?.id ?? null}
      noteType="Group"
      prefill={{
        appointmentKey: view.appointmentKey,
        date: sessionDate,
        time: when.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
        durationMin: view.durationMin,
        sessionType: 'Group Therapy',
      }}
      history={history}
      group={{
        sessionId: view.id,
        format: view.format,
        bar,
        bindSave: (fn) => {
          saveRef.current = fn;
        },
        members: otherMembers(view.attendees, meA.contactId),
        summaryWritten,
        shared: sharedFor(meA.contactId),
        onSigned: nextAfterSign,
        readOnly: ro,
        onPersist: notify,
      }}
      contactId={meA.contactId}
      contactName={meA.name}
      users={users}
      currentUserId={me}
      canReviewNotes={isManager}
      onClose={onClose}
      onSaved={() => void reload()}
    />
  );
}
